import { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';

function AddCustomerModal({ onClose }) {
  const { addCustomer } = useApp();
  const [form, setForm] = useState({ name: '', contact: '', addr: '', phone: '', email: '', notes: '', status: 'active' });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    addCustomer(form);
    onClose();
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">Add Customer / GC</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="field"><label>Company Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Pinnacle Construction Group" required /></div>
            <div className="form-row">
              <div className="field"><label>Contact Name</label><input value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="Primary contact" /></div>
              <div className="field">
                <label>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="at_risk">At Risk</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="field"><label>Address</label><input value={form.addr} onChange={e => set('addr', e.target.value)} placeholder="1234 Commerce St, City, TX 75001" /></div>
            <div className="form-row">
              <div className="field"><label>Phone</label><input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 555-5555" /></div>
              <div className="field"><label>Email</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="contact@gc.com" /></div>
            </div>
            <div className="field"><label>Notes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Payment terms, special requirements…" /></div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Add Customer</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Customers() {
  const { customers, invoices, jobs, deleteCustomer } = useApp();
  const [search, setSearch] = useState('');
  const [modal, setModal]   = useState(null);

  const filtered = useMemo(() =>
    customers.filter(c =>
      !search ||
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.contact?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase())
    ),
    [customers, search]
  );

  const statusColor = (s) => ({
    active:   { bg: 'var(--teal-t)',  color: 'var(--teal)'  },
    at_risk:  { bg: 'var(--amber-t)', color: 'var(--amber)' },
    inactive: { bg: 'var(--s3)',      color: 'var(--t4)'    },
  }[s] || { bg: 'var(--s3)', color: 'var(--t4)' });

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Customers & GCs</div>
          <div className="psub">{customers.length} contact{customers.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="pacts">
          <button className="btn btn-primary" onClick={() => setModal({ type: 'add' })}>+ Add Customer</button>
        </div>
      </div>

      <div className="card">
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--b1)' }}>
          <div className="search-bar">
            <span>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, contact, or email…" />
          </div>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Company</th>
              <th>Contact</th>
              <th>Phone / Email</th>
              <th>Open Invoices</th>
              <th>Active Jobs</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--t3)', padding: '30px' }}>No customers yet — add your first GC to get started.</td></tr>
            )}
            {filtered.map(cust => {
              const sc = statusColor(cust.status);
              const openInvs = invoices.filter(i => i.customerId === cust.id && (i.status === 'sent' || i.status === 'overdue'));
              const overdueInvs = openInvs.filter(i => i.status === 'overdue');
              const activeJobs = jobs.filter(j => j.customerId === cust.id && j.status === 'active');
              return (
                <tr key={cust.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{cust.name}</div>
                    {cust.addr && <div className="tbl-sub">{cust.addr}</div>}
                  </td>
                  <td style={{ color: 'var(--t2)' }}>{cust.contact || '—'}</td>
                  <td>
                    <div style={{ fontSize: 12, color: 'var(--t3)' }}>{cust.phone || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--t3)' }}>{cust.email || '—'}</div>
                  </td>
                  <td>
                    {openInvs.length === 0
                      ? <span style={{ color: 'var(--t4)', fontSize: 12 }}>—</span>
                      : (
                        <span className="bdg" style={{ background: overdueInvs.length ? 'var(--rose-t)' : 'var(--amber-t)', color: overdueInvs.length ? 'var(--rose)' : 'var(--amber)' }}>
                          {overdueInvs.length ? `⚠ ${overdueInvs.length} overdue` : `${openInvs.length} open`}
                        </span>
                      )
                    }
                  </td>
                  <td>
                    {activeJobs.length === 0
                      ? <span style={{ color: 'var(--t4)', fontSize: 12 }}>—</span>
                      : <span className="bdg" style={{ background: 'var(--cyan-t)', color: 'var(--cyan)' }}>{activeJobs.length} active</span>
                    }
                  </td>
                  <td><span className="bdg" style={{ background: sc.bg, color: sc.color }}>{cust.status || 'active'}</span></td>
                  <td>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--rose)' }}
                      onClick={() => { if (window.confirm(`Remove ${cust.name}?`)) deleteCustomer(cust.id); }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal?.type === 'add' && <AddCustomerModal onClose={() => setModal(null)} />}
    </div>
  );
}
